import React, { useEffect, useState } from 'react';
import { Alert, Avatar, Button, Card, Col, Form, Input, Row, Upload, Typography, message } from 'antd';
import { LockOutlined, SaveOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { changePassword, updateProfile } from '../store/slices/authSlice';
import PageShell from '../components/Layout/PageShell';
import uploadService from '../services/uploadService';

const { Text, Title } = Typography;

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>(user?.profile?.avatar || '');

  useEffect(() => {
    profileForm.setFieldsValue({
      firstName: user?.profile?.firstName || '',
      lastName: user?.profile?.lastName || '',
      email: user?.email || '',
      phone: user?.profile?.phone || '',
      bio: user?.profile?.bio || '',
    });
    setAvatarUrl(user?.profile?.avatar || '');
  }, [profileForm, user]);

  const onSaveProfile = async (values: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    bio?: string;
  }) => {
    setProfileSaving(true);
    try {
      const result = await dispatch(
        updateProfile({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim().toLowerCase(),
          phone: values.phone?.trim() || '',
          bio: values.bio?.trim() || '',
          avatar: avatarUrl || '',
        })
      );
      if (updateProfile.fulfilled.match(result)) {
        message.success('Profile updated successfully.');
      } else {
        message.error((result.payload as string) || 'Unable to update profile.');
      }
    } catch {
      message.error('Unable to update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      if (!uploadService.validateImageFile(file)) {
        message.error('Please upload a valid image file (JPEG, PNG, WebP)');
        return false;
      }
      if (!uploadService.validateFileSize(file, 5)) {
        message.error('Image size must be less than 5MB');
        return false;
      }

      setAvatarUploading(true);
      const result = await uploadService.uploadImage(file);
      setAvatarUrl(result.url);
      message.success('Profile picture uploaded. Save profile to apply.');
      return false;
    } catch (error: any) {
      message.error(error?.message || 'Failed to upload profile picture');
      return false;
    } finally {
      setAvatarUploading(false);
    }
  };

  const onResetPassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    setPasswordSaving(true);
    try {
      const result = await dispatch(
        changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        })
      );
      if (changePassword.fulfilled.match(result)) {
        message.success('Password changed successfully.');
        passwordForm.resetFields();
      } else {
        message.error((result.payload as string) || 'Unable to change password.');
      }
    } catch {
      message.error('Unable to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <PageShell
      title="Profile"
      description="Manage your account details and security settings."
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card className="mb-4 rounded-2xl border-slate-200 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Avatar
                  size={72}
                  src={avatarUrl || user?.profile?.avatar}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#0171BF' }}
                />
                <div>
                  <p className="mb-0 text-sm font-semibold text-slate-900">Profile picture</p>
                  <Text type="secondary" className="text-xs sm:text-sm">
                    Upload a clear photo or business logo.
                  </Text>
                </div>
              </div>
              <Upload showUploadList={false} beforeUpload={handleAvatarUpload} accept="image/*">
                <Button icon={<UploadOutlined />} loading={avatarUploading}>
                  Upload picture
                </Button>
              </Upload>
            </div>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm" styles={{ body: { paddingBottom: 16 } }}>
            <div className="mb-4 flex items-center gap-2">
              <UserOutlined className="text-[#0171BF]" />
              <Title level={4} className="!m-0 !text-base sm:!text-lg">
                Personal details
              </Title>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              requiredMark={false}
              onFinish={onSaveProfile}
              className="vendor-comfort-form"
            >
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label="First name"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <Input placeholder="First name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label="Last name"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <Input placeholder="Last name" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="email"
                label="Email address"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Enter a valid email address' },
                ]}
              >
                <Input placeholder="you@example.com" />
              </Form.Item>

              <Form.Item name="phone" label="Phone number">
                <Input placeholder="+91..." />
              </Form.Item>

              <Form.Item name="bio" label="Bio">
                <Input.TextArea rows={4} maxLength={500} showCount placeholder="Short introduction about you..." />
              </Form.Item>

              <div className="flex justify-end">
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={profileSaving} className="bg-[#0171BF]">
                  Save profile
                </Button>
              </div>
            </Form>
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <LockOutlined className="text-[#0171BF]" />
              <Title level={4} className="!m-0 !text-base sm:!text-lg">
                Reset password
              </Title>
            </div>
            <Alert
              showIcon
              type="info"
              className="mb-4"
              message="Use a strong password with uppercase, lowercase, number, and special character."
            />
            <Form form={passwordForm} layout="vertical" requiredMark={false} onFinish={onResetPassword}>
              <Form.Item
                name="currentPassword"
                label="Current password"
                rules={[{ required: true, message: 'Please enter current password' }]}
              >
                <Input.Password placeholder="Current password" />
              </Form.Item>
              <Form.Item
                name="newPassword"
                label="New password"
                rules={[
                  { required: true, message: 'Please enter new password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).+/,
                    message: 'Must include uppercase, lowercase, number, and special character',
                  },
                ]}
              >
                <Input.Password placeholder="New password" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm new password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm new password" />
              </Form.Item>

              <div className="flex justify-end">
                <Button type="primary" htmlType="submit" loading={passwordSaving} className="bg-[#0171BF]">
                  Update password
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>

      <div className="mt-3">
        <Text type="secondary" className="text-xs sm:text-sm">
          Account role: <strong>{user?.role || 'vendor'}</strong>
        </Text>
      </div>
    </PageShell>
  );
};

export default Profile;
